import { CameraInitParams } from './types';

export class Camera {
  canvas: HTMLCanvasElement;
  video: HTMLVideoElement;
  context: CanvasRenderingContext2D;
  stream?: MediaStream;
  ended = false;

  onError?: (e: Error) => void;

  constructor(canvas: HTMLCanvasElement, video: HTMLVideoElement) {
    this.canvas = canvas;
    this.video = video;
    const context = canvas.getContext('2d');
    if (!context) {
      throw Error('Can not create context');
    }
    this.context = context;
  }

  private async getStream() {
    const config = {
      audio: false,
      video: {
        width: { min: 640, ideal: 1280, max: 1280 },
        height: { min: 480, ideal: 720, max: 720 },
      },
      frameRate: {
        ideal: 24,
      },
    };
    const stream = await navigator.mediaDevices.getUserMedia(config);
    (stream as any).oninactive = async (ev: any) => {
      if (!this.ended) {
        this.restartStream();
      }
    };
    return stream;
  }

  finishTracks(stream: MediaStream) {
    stream.getTracks().forEach((t) => t.stop());
  }

  async restartStream() {
    try {
      if (this.stream) {
        this.finishTracks(this.stream);
      }
      this.stream = await this.getStream();
      this.setupStrem(this.stream);
    } catch (e: any) {
      this.onError?.(e);
    }
  }

  private async setupStrem(stream: MediaStream) {
    this.video.srcObject = stream;
    await new Promise<undefined>((res) => {
      this.video.onloadedmetadata = () => {
        res(undefined);
      };
    });

    this.video.play();
    const videoWidth = this.video.videoWidth;
    const videoHeight = this.video.videoHeight;

    this.video.width = videoWidth;
    this.video.height = videoHeight;

    this.canvas.width = this.video.width;
    this.canvas.height = this.video.height;

    this.context.translate(this.canvas.width, 0);
    this.context.scale(-1, 1);

    this.renderPrediction();
  }

  static async init(params: CameraInitParams, onError?: (e: Error) => void) {
    const camera = new Camera(params.canvas, params.video);
    camera.onError = onError;
    await camera.restartStream();

    return camera;
  }

  async renderPrediction() {
    if (this.video.readyState < 2) {
      await new Promise((resolve) => {
        this.video.onloadeddata = () => {
          resolve(undefined);
        };
      });
    }
    this.drawCtx();
    requestAnimationFrame(this.renderPrediction.bind(this));
  }

  async finish() {
    this.ended = true;
    this.video.pause();
    if (this.stream) {
      this.finishTracks(this.stream);
      this.stream = undefined;
    }
    this.video.srcObject = null;
  }

  drawCtx() {
    const width = this.video.width / (this.canvas.width / this.video.width);
    const height =
      this.video.height / (this.canvas.height / this.canvas.height);
    this.context.drawImage(this.video, 0, 0, width, height);
  }
}
